import React from 'react';
import { motion } from 'framer-motion';
import { ProgressIndicator } from './ProgressIndicator';
interface SlideTensionProps {
  onNext: () => void;
}
function ModalityShapes() {
  return <svg viewBox="0 0 200 200" className="w-full h-full" aria-hidden="true" data-id="element-243">
      {/* Different modality shapes - abstract, varied */}
      <motion.circle cx="60" cy="60" r="25" fill="none" stroke="#94A3B8" strokeWidth="1.5" strokeDasharray="4 4" initial={{
      opacity: 0,
      scale: 0.5
    }} animate={{
      opacity: 0.8,
      scale: 1
    }} transition={{
      duration: 1,
      delay: 0.3
    }} data-id="element-244" />
      <motion.rect x="100" y="40" width="40" height="40" rx="4" fill="none" stroke="#94A3B8" strokeWidth="1.5" strokeDasharray="4 4" initial={{
      opacity: 0,
      scale: 0.5
    }} animate={{
      opacity: 0.7,
      scale: 1
    }} transition={{
      duration: 1,
      delay: 0.5
    }} data-id="element-245" />
      <motion.polygon points="80,130 100,95 120,130" fill="none" stroke="#94A3B8" strokeWidth="1.5" strokeDasharray="4 4" initial={{
      opacity: 0,
      scale: 0.5
    }} animate={{
      opacity: 0.6,
      scale: 1
    }} transition={{
      duration: 1,
      delay: 0.7
    }} data-id="element-246" />
      <motion.line x1="30" y1="170" x2="170" y2="170" stroke="#CBD5E1" strokeWidth="1.5" strokeDasharray="2 6" initial={{
      pathLength: 0
    }} animate={{
      pathLength: 1
    }} transition={{
      duration: 1.5,
      delay: 1
    }} data-id="element-247" />
    </svg>;
}
function ControlLine() {
  return <svg viewBox="0 0 200 200" className="w-full h-full" aria-hidden="true" data-id="element-248">
      {/* Stable continuous line representing consistent control */}
      <motion.path d="M10,140 C30,138 50,100 70,80 C90,60 110,45 130,42 C150,40 170,40 190,40" fill="none" stroke="#00B4D8" strokeWidth="2.5" strokeLinecap="round" initial={{
      pathLength: 0,
      opacity: 0
    }} animate={{
      pathLength: 1,
      opacity: 1
    }} transition={{
      duration: 2,
      delay: 0.5,
      ease: 'easeInOut'
    }} data-id="element-249" />
      {/* Glow duplicate */}
      <motion.path d="M10,140 C30,138 50,100 70,80 C90,60 110,45 130,42 C150,40 170,40 190,40" fill="none" stroke="#00B4D8" strokeWidth="6" strokeLinecap="round" opacity="0.15" filter="blur(4px)" initial={{
      pathLength: 0,
      opacity: 0
    }} animate={{
      pathLength: 1,
      opacity: 0.15
    }} transition={{
      duration: 2,
      delay: 0.5,
      ease: 'easeInOut'
    }} data-id="element-250" />
      {/* Endpoint dot */}
      <motion.circle cx="190" cy="40" r="4" fill="#00B4D8" initial={{
      opacity: 0,
      scale: 0
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      delay: 2.5,
      duration: 0.4
    }} data-id="element-251" />
      {/* Axis labels */}
      <motion.text x="10" y="195" fill="#94A3B8" fontSize="9" fontFamily="Inter, sans-serif" initial={{
      opacity: 0
    }} animate={{
      opacity: 0.8
    }} transition={{
      delay: 1.5
    }} data-id="element-252">
        Time →
      </motion.text>
      <motion.text x="160" y="32" fill="#0891B2" fontSize="9" fontFamily="Inter, sans-serif" fontWeight="600" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 2.8
    }} data-id="element-253">
        Control
      </motion.text>
    </svg>;
}
export function SlideTension({
  onNext
}: SlideTensionProps) {
  return <div className="relative w-full h-full flex flex-col items-center justify-center px-6" data-id="element-254">
      <div className="relative z-10 max-w-4xl mx-auto w-full flex flex-col items-center" data-id="element-255">
        {/* Text section */}
        <div className="text-center mb-8 max-w-2xl" data-id="element-256">
          <motion.h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-400 leading-snug mb-4" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7
        }} data-id="element-257">
            The question is no longer just how treatment is given
          </motion.h2>

          <motion.p className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 leading-snug" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.5
        }} data-id="element-258">
            But how consistently it{' '}
            <span className="text-teal-600" data-id="element-259">controls disease over time</span> —
            in real patients.
          </motion.p>
        </div>

        {/* Split visual */}
        <motion.div className="w-full grid grid-cols-2 gap-6 max-w-lg mb-10" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.8
      }} data-id="element-260">
          <div className="aspect-square rounded-xl border border-slate-200 bg-slate-50 p-4 flex flex-col" data-id="element-261">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 mb-2" data-id="element-262">
              Different modalities
            </span>
            <div className="flex-1" data-id="element-263">
              <ModalityShapes data-id="element-264" />
            </div>
          </div>
          <div className="aspect-square rounded-xl border border-teal-200 bg-slate-50 p-4 flex flex-col" data-id="element-265">
            <span className="text-[10px] uppercase tracking-widest text-teal-600/70 mb-2" data-id="element-266">
              Control over time
            </span>
            <div className="flex-1" data-id="element-267">
              <ControlLine data-id="element-268" />
            </div>
          </div>
        </motion.div>

        <motion.button onClick={onNext} className="px-8 py-3.5 bg-takeda-red text-white font-semibold rounded-lg text-base
                     hover:bg-takeda-darkred transition-all duration-300
                     shadow-lg shadow-red-500/20 
                     focus:outline-none focus:ring-2 focus:ring-takeda-red focus:ring-offset-2 focus:ring-offset-white" initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 1.2
      }} whileHover={{
        scale: 1.03
      }} whileTap={{
        scale: 0.98
      }} aria-label="Explore the full discussion — proceed to next slide" data-id="element-269">
          Explore the full discussion
        </motion.button>

        <motion.div className="mt-10" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.5,
        duration: 0.6
      }} data-id="element-270">
          <ProgressIndicator currentStep={6} totalSteps={7} data-id="element-271" />
        </motion.div>
      </div>
    </div>;
}